import { useState } from "react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MapPin, Phone, Clock, Mail } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const contactInfo = [
  {
    icon: MapPin,
    label: "Visit Us",
    value: "22560 Telegraph Rd",
    sub: "Southfield, MI",
  },
  {
    icon: Phone,
    label: "Call Us",
    value: "(313) 484-7307",
    sub: "Available 24/7",
  },
  {
    icon: Clock,
    label: "Business Hours",
    value: "6 AM - 9 PM",
    sub: "7 days a week",
  },
  {
    icon: Mail,
    label: "Email",
    value: "booking@afromagicbraiding.shop",
    sub: "We reply within 24 hours",
  },
];

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll be in touch.");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-24 md:py-32 bg-brand-surface relative noise-overlay"
      data-testid="contact-section"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <p className="text-xs uppercase tracking-[0.2em] font-sans text-brand-gold mb-4">
            Get in Touch
          </p>
          <h2 className="font-serif text-3xl md:text-5xl text-brand-text tracking-tight">
            Contact Us
          </h2>
          <div className="gold-line-long mx-auto mt-6" />
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info Cards */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {contactInfo.map((item, i) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.label}
                    className="p-5 border border-brand-border bg-brand-bg group hover:border-brand-gold/30 transition-colors"
                    data-testid={`contact-info-${i}`}
                  >
                    <Icon
                      size={20}
                      className="text-brand-gold mb-3"
                    />
                    <p className="font-sans text-xs text-brand-text-muted uppercase tracking-wider mb-1">
                      {item.label}
                    </p>
                    <p className="font-sans text-sm text-brand-text font-medium">
                      {item.value}
                    </p>
                    <p className="font-sans text-xs text-brand-text-muted mt-1">
                      {item.sub}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Social Links */}
            <div className="mt-8">
              <p className="font-sans text-xs uppercase tracking-wider text-brand-text-muted mb-4">
                Follow Us
              </p>
              <div className="flex gap-4">
                <a
                  href="https://www.instagram.com/afromagicbraidinghair"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="social-instagram"
                  className="w-10 h-10 border border-brand-border flex items-center justify-center text-brand-text-muted hover:text-brand-gold hover:border-brand-gold transition-colors"
                >
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                  </svg>
                </a>
                <a
                  href="https://www.facebook.com/share/1CK3NjZq6S/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="social-facebook"
                  className="w-10 h-10 border border-brand-border flex items-center justify-center text-brand-text-muted hover:text-brand-gold hover:border-brand-gold transition-colors"
                >
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
                <a
                  href="https://www.tiktok.com/@afro.magic.braiding.hair"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="social-tiktok"
                  className="w-10 h-10 border border-brand-border flex items-center justify-center text-brand-text-muted hover:text-brand-gold hover:border-brand-gold transition-colors"
                >
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
                    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
                  </svg>
                </a>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="space-y-4"
            data-testid="contact-form"
          >
            <div>
              <label className="font-sans text-xs text-brand-text-muted block mb-1.5">
                Full Name *
              </label>
              <Input
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Your name"
                data-testid="contact-name-input"
                className="bg-brand-bg border-brand-border text-brand-text placeholder:text-brand-text-muted/50 focus:border-brand-gold rounded-none"
              />
            </div>
            <div>
              <label className="font-sans text-xs text-brand-text-muted block mb-1.5">
                Email *
              </label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder="your@email.com"
                data-testid="contact-email-input"
                className="bg-brand-bg border-brand-border text-brand-text placeholder:text-brand-text-muted/50 focus:border-brand-gold rounded-none"
              />
            </div>
            <div>
              <label className="font-sans text-xs text-brand-text-muted block mb-1.5">
                Phone
              </label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                placeholder="(555) 123-4567"
                data-testid="contact-phone-input"
                className="bg-brand-bg border-brand-border text-brand-text placeholder:text-brand-text-muted/50 focus:border-brand-gold rounded-none"
              />
            </div>
            <div>
              <label className="font-sans text-xs text-brand-text-muted block mb-1.5">
                Message *
              </label>
              <Textarea
                value={formData.message}
                onChange={(e) =>
                  setFormData({ ...formData, message: e.target.value })
                }
                placeholder="Tell us how we can help..."
                data-testid="contact-message-input"
                className="bg-brand-bg border-brand-border text-brand-text placeholder:text-brand-text-muted/50 focus:border-brand-gold rounded-none min-h-[120px]"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-btn"
              className="w-full py-3 bg-brand-gold text-black font-sans font-semibold text-sm hover:bg-brand-gold-light transition-colors disabled:opacity-60"
            >
              {loading ? "Sending..." : "Send Message"}
            </button>
          </motion.form>

          {/* Google Maps Embed */}
          <div className="mt-8 border border-brand-border overflow-hidden" data-testid="contact-map">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2941.8!2d-83.2219!3d42.4734!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2s22560+Telegraph+Rd%2C+Southfield%2C+MI+48033!5e0!3m2!1sen!2sus!4v1"
              width="100%"
              height="250"
              style={{ border: 0, filter: "grayscale(0.3) contrast(1.1)" }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Afro Magic Braiding Hair - 22560 Telegraph Rd, Southfield, MI 48033"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
